import {
  addChangedItem,
  setActiveEmptySpecification,
  setActiveSpecificationItem,
  setOpenCategories,
} from "@/core/redux/slices/specification";
import { useDispatch, useSelector } from "@/core/redux/store";
import { Input } from "@/shared/components/ui/input";
import { specificationService } from "@/shared/services/api/specification-service";
import {
  ISpecificationCategoryResponse,
  ISpecificationCategoryOrItemResponse,
  ISpecificationItemResponse,
  ISpecificationItemResponseExtended,
} from "@/shared/services/interface/responses/ISpecificationResponse";
import { debounce } from "lodash";
import { ChevronRight } from "lucide-react";
import { useCallback, useEffect, useRef, useMemo } from "react";
import { useDrag } from "react-dnd";
import { SpecificationCategoryList } from "../category-list";
import { CategoryItem } from "../category-item/category-item";
import { getSpecificationThunk } from "@/core/redux/thunks/specificationThunk";
import CollapsibleItemMenuView from "../../../../../../components/sidebar/collapsible-item-menu/collapsible-item-menu-view";
import CustomToast from "@/shared/hooks/custom-toast";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import { setIsOpenEditPopover } from "@/core/redux/slices/popover";
import { useTreeDnD } from "../../hooks/tree-dnd-provider/useTreeDnD";
import { DND_TYPE } from "../../hooks/tree-dnd-provider/TreeDnDProvider";
import { cn } from "@/lib/utils";
import { findAllParentCategoryIds } from "../../utils/category-helpers";
import { categoryMatchesSearch, itemMatchesSearch } from "@/core/redux/helper/specification";
import { capitalizeWords } from "@/shared/helpers/text-helper";

export default function RenderCollapsible({
  item,
  priority,
  parentId = null,
  prevItemId = null,
  nextItemId = null,
}: {
  item: ISpecificationCategoryResponse;
  priority: string;
  disableEdit?: boolean;
  parentId?: string | null;
  prevItemId?: string | null;
  nextItemId?: string | null;
}) {
  const [searchParams] = useSearchParams();
  const isIframe = searchParams.get("iframe") === "true";
  const searchTerm = useSelector((state) => state.specification.searchTerm);

  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { getPreviewClass } = useTreeDnD();

  const specification = useSelector((s) => s.specification);
  const sidebarWidth = useSelector((s) => s.specificationList.sidebarWidth);
  const user = useSelector((s) => s.user.me);

  // Best Practice: Use primitive dependency (rerender-dependencies)
  // Check if this category should be highlighted (recently updated via streaming)
  const isHighlighted = specification.highlightedCategoryIds.includes(item.id);

  const activeCategory = specification.activeSpecificationStep.category || {
    id: "",
  };
  const filteredCategories = specification.filteredCategories;

  // Find all active category IDs (selected item's category + all parents)
  const activeCategoryIds = useMemo(() => {
    if (
      !activeCategory?.id ||
      !filteredCategories ||
      filteredCategories.length === 0
    ) {
      return [];
    }
    return findAllParentCategoryIds(filteredCategories, activeCategory.id);
  }, [activeCategory?.id, filteredCategories]);

  // Check if this category is active (either directly selected or a parent of selected item)
  const isCategoryActive = useMemo(() => {
    return activeCategoryIds.includes(item.id);
  }, [activeCategoryIds, item.id]);

  // Get isOpen state directly from Redux to stay in sync
  const isOpen = specification.openCategories.includes(item.id);

  // -------------------------------------
  // NEW ITEM
  // -------------------------------------
  const handleAddNewItem = (focus = false) => {
    const existingBlankDraft = specification.changedValues.items.find(
      (draft) =>
        draft.isNew &&
        draft.categoryId === item.id &&
        !draft.description?.trim()
    );

    const isActiveBlankDraftInCategory =
      specification.activeSpecificationStep?.isNew &&
      specification.activeSpecificationStep?.category?.id === item.id &&
      !specification.activeSpecificationStep?.description?.trim() &&
      !!specification.activeSpecificationStep?.id;

    if (existingBlankDraft || isActiveBlankDraftInCategory) {
      const draftToFocus: ISpecificationItemResponseExtended = existingBlankDraft
        ? {
            id: existingBlankDraft.id,
            type: "item",
            description: existingBlankDraft.description || "",
            score: existingBlankDraft.score,
            position: "",
            categoryId: item.id,
            category: {
              id: item.id,
            },
            orderNo: existingBlankDraft.orderNo,
            isActive: true,
            createdAt: existingBlankDraft.createdAt,
            updatedAt: existingBlankDraft.updatedAt,
            deletedAt: existingBlankDraft.deletedAt,
            isNew: true,
          }
        : {
            ...specification.activeSpecificationStep,
            categoryId: item.id,
            category: {
              id: item.id,
            },
            isNew: true,
          };

      dispatch(setActiveSpecificationItem(draftToFocus));
      dispatch(setIsOpenEditPopover(true));

      if (focus) {
        setTimeout(() => {
          const el = document.getElementById("new-question-input");
          el?.focus();
        }, 50);
      }
      return;
    }

    const id = uuidv4();
    const existingItemsCount = (item.children || []).filter(
      (child) => child.type === "item"
    ).length;
    const nextOrderNo = `${priority}.${existingItemsCount + 1}`;

    const newItem: ISpecificationItemResponseExtended = {
      id,
      type: "item",
      description: "",
      score: 5, // Default score
      position: "a0", // Default position
      categoryId: item.id,
      category: {
        id: item.id,
      },
      orderNo: nextOrderNo,
      isActive: true,
      createdAt: new Date().toISOString(),
      isNew: true,
    };

    dispatch(addChangedItem(newItem));
    dispatch(setActiveSpecificationItem(newItem));
    dispatch(setIsOpenEditPopover(true));

    if (focus) {
      setTimeout(() => {
        const el = document.getElementById("new-question-input");
        el?.focus();
      }, 50);
    }
  };

  const openCategory = () => {
    dispatch(setOpenCategories(item.id));
  };

  const openCategoryRef = useRef(openCategory);
  openCategoryRef.current = openCategory;

  useEffect(() => {
    if (specification.specification.specification.status === 1 && !isOpen) {
      openCategoryRef.current();
    }
  }, [
    specification.specification.specification.status,
    isOpen,
    item.id,
    dispatch,
    specification.openCategories,
  ]);

  // -------------------------------------
  // CATEGORY UPDATE
  // -------------------------------------
  const updateCategory = useCallback(
    debounce(async (categoryId: string, name: string) => {
      if (isIframe || !user?.company.id) return;

      const response = await specificationService.updateSpecificationCategory(
        {
          categoryId,
          name,
          companyId: user.company.id,
          specificationId: specification.specification.specification.id,
        },
        false
      );

      if (response.success) {
        CustomToast().success({
          title: t("category-updated"),
          position: "bottom-right",
        });
        dispatch(
          getSpecificationThunk({
            specificationId: specification.specification.specification.id,
            companyId: user.company.id,
            userId: user.id,
          })
        );
      } else {
        CustomToast().error({
          title: t("category-update-error"),
          position: "bottom-right",
        });
      }
    }, 800),
    [user, specification]
  );

  // -------------------------------------
  // TOGGLE (only arrow icon)
  // -------------------------------------
  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation();

    dispatch(
      setActiveEmptySpecification({
        categoryId: item.id,
        isNew: false,
      })
    );

    // setOpenCategories already toggles the category in Redux state
    openCategoryRef.current();
  };

  // -------------------------------------
  // DRAG (minimal - only to start drag)
  // -------------------------------------
  const [{ isDragging }, dragRef] = useDrag(() => ({
    type: DND_TYPE,
    item: {
      id: item.id,
      nodeType: "CATEGORY" as const,
      parentId: parentId,
    },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  }));

  // Get preview class
  const previewClass = getPreviewClass(item.id);
  const hasChildren =
    item.children && Array.isArray(item.children) && item.children.length > 0;
  const makeChildClassRef = useRef<boolean>(false);
  useEffect(() => {
    const isPreviewingAsChild = previewClass === "dnd-make-child";
    let timer = null;
    if (previewClass && isPreviewingAsChild && !makeChildClassRef.current) {
      makeChildClassRef.current = true;
      // start timer to check if still exists for 2 seconds
      timer = setTimeout(() => {
        if (makeChildClassRef.current) {
          makeChildClassRef.current = false;
          if (isOpen) return;
          openCategoryRef.current();
        }
      }, 600);
    }
    if ((!previewClass || !isPreviewingAsChild) && makeChildClassRef.current) {
      makeChildClassRef.current = false;
    }
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [previewClass, isOpen]);

  return (
    <div className="relative">
      <div
        ref={dragRef}
        data-node-id={item.id}
        data-node-type="CATEGORY"
        data-node-parent-id={parentId || "null"}
        data-node-prev-item-id={prevItemId || "null"}
        data-node-next-item-id={nextItemId || "null"}
        className={cn(
          "relative flex text-xs items-center w-full min-h-[45px] py-1 cursor-pointer mb-[4px] border rounded-md transition-colors duration-200",
          isCategoryActive ? "border-zinc-200 bg-white" : "border-transparent",
          isDragging && "opacity-50",
          !(isOpen && hasChildren) && previewClass,
          isHighlighted && "category-highlight-pulse"
        )}
        onClick={handleToggle}
      >
        {/* TOGGLE ICON */}
        <button className="h-7 w-7 flex items-center justify-center">
          <ChevronRight
            className={`h-4 w-4 transition-transform ${isOpen ? "rotate-90" : ""
              }`}
          />
        </button>

        {/* PRIORITY */}
        <span
          className={`text-xs font-500 mr-2 ${isCategoryActive ? "category-active-color" : ""
            }`}
        >
          {priority}.
        </span>

        {/* NAME */}
        <div className="flex-1 truncate text-xs max-w-[calc(100%-100px)]">
          {capitalizeWords(item.name)}
        </div>

        {/* MENU */}
        <CollapsibleItemMenuView
          item={item}
          updateCategory={updateCategory}
          priority={priority}
          isOpen={isOpen}
          isCategoryActive={isCategoryActive}
          handleAddNewItem={handleAddNewItem}
        />
      </div>

      {/* CHILDREN */}
      {isOpen && hasChildren && item.children && (
        <div
          className={cn(
            "pl-[12px] flex flex-col border-l border-gray-200 ml-0",
            previewClass
          )}
        >
          {item.children.map((child, index) => {
            // Check if child matches search - if not, don't render
            if (child.type === "category") {
              if (!categoryMatchesSearch(child as ISpecificationCategoryResponse, searchTerm)) {
                return null;
              }
            } else if (child.type === "item") {
              if (!itemMatchesSearch(child as ISpecificationItemResponse, searchTerm)) {
                return null;
              }
            }

            const mainArr =
              item.children as ISpecificationCategoryOrItemResponse[];

            const prevItemId = index === 0 ? null : mainArr[index - 1].id;
            const nextItemId =
              index === mainArr.length - 1 ? null : mainArr[index + 1].id;
            if (child.type === "category") {
              // Render subcategory - pass parent ID (current category's ID)
              return (
                <SpecificationCategoryList
                  key={child.id}
                  categories={[child as ISpecificationCategoryResponse]}
                  parentPriority={priority}
                  parentId={item.id}
                  subIndex={index}
                  prevItemId={prevItemId}
                  nextItemId={nextItemId}
                />
              );
            } else if (child.type === "item") {
              // Render item
              return (
                <CategoryItem
                  key={child.id}
                  subItem={child}
                  sidebarWidth={sidebarWidth}
                  priority={priority}
                  subIndex={
                    item.children
                      ?.filter((c) => c.type === "item")
                      .indexOf(child) || 0
                  }
                  item={item}
                  prevItemId={prevItemId}
                  nextItemId={nextItemId}
                />
              );
            } else {
              return null;
            }
          })}
        </div>
      )}
    </div>
  );
}
